import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import Link from '../../components/Link';
import PageTitle from '../../components/PageTitle';
import styles from '../styles/produtos/Sacola.module.css';
import NotificacaoSucesso from '../../components/NotificacaoSucesso';
import NotificacaoErro from '../../components/NotificacaoErro';
import ModalConfirmacao from '../../components/ModalConfirmacao';
import {
  MdDeleteOutline,
  MdOutlineFileDownload,
  MdOutlineFileUpload,
  MdOutlineInventory2,
  MdOutlinePrint,
  MdOutlineShoppingCartCheckout,
  MdShoppingBag,
} from 'react-icons/md';

const formatarReal = (valor) => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
}).format(Number(valor || 0));

const SEM_ESTOQUE_ALVO = 'sem-estoque-alvo';

const Sacola = () => {
  const permissoes = JSON.parse(localStorage.getItem('permissoes'));
  const token = localStorage.getItem('token');
  const usuario = localStorage.getItem('usuario');

  const [pesoSacola, setPesoSacola] = useState('');
  const [precoSacola, setPrecoSacola] = useState('');

  const [produtos, setProdutos] = useState([]);
  const [quantidades, setQuantidades] = useState({});
  const [anotacoes, setAnotacoes] = useState({});
  const [estoques, setEstoques] = useState({});
  const [estoquesAlvoSacola, setEstoquesAlvoSacola] = useState([]);
  const [estoqueAlvoSelecionado, setEstoqueAlvoSelecionado] = useState('');

  const [mensagemNotificacao, setMensagemNotificacao] = useState('');
  const [notificacaoSucesso, setNotificacaoSucesso] = useState(false);
  const [notificacaoErro, setNotificacaoErro] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [onConfirmAction, setOnConfirmAction] = useState(null);
  const [isModalPedidoOpen, setIsModalPedidoOpen] = useState(false);
  const [observacaoPedido, setObservacaoPedido] = useState('');

  const fileInputRef = useRef(null);

  const [mostrarTotalSacola, setMostrarTotalSacola] = useState(false);
  const [mostrarPreco, setMostrarPreco] = useState(false);
  const [mostrarPeso, setMostrarPeso] = useState(false);
  const [mostrarCustoFinal, setMostrarCustoFinal] = useState(false);
  const [mostrarEstoques, setMostrarEstoques] = useState(permissoes.produtos.sacola_estoquesAbertoPadrao || false);
  const [ordenacao, setOrdenacao] = useState('data_desc');
  const sacolaSemEstoqueAlvo = estoqueAlvoSelecionado === SEM_ESTOQUE_ALVO;
  const tituloSacolaSelecionada = sacolaSemEstoqueAlvo
    ? 'Sacola sem estoque alvo'
    : `Sacola da empresa ${estoqueAlvoSelecionado || '-'}`;

  const handleToggleTotal = () => {
    setMostrarTotalSacola(prev => !prev);
  };

  const handleTogglePreco = () => {
    setMostrarPreco(prev => !prev);
  };

  const handleTogglePeso = () => {
    setMostrarPeso(prev => !prev);
  };

  const handleToggleCustoFinal = () => {
    setMostrarCustoFinal(prev => !prev);
  };

  const handleToggleEstoques = () => {
    setMostrarEstoques(prev => !prev);
  };

  const montarMensagemEstoqueInsuficiente = (itens = []) => {
    if (!Array.isArray(itens) || itens.length === 0) {
      return 'Existem itens sem quantidade suficiente no estoque alvo para finalizar o pedido.';
    }

    const detalhes = itens
      .slice(0, 3)
      .map((item) => `${item.codigo}: empresa ${item.estoque_alvo}, sacola ${item.quantidade}, disponível ${item.qtd_disponivel_finalizacao}`)
      .join(' | ');

    const sufixo = itens.length > 3 ? ` (+${itens.length - 3} itens)` : '';

    return `Estoque insuficiente: ${detalhes}${sufixo}`;
  };

  // Buscar os produtos na sacola
  const fetchSacola = async (estoqueAlvo = estoqueAlvoSelecionado) => {
    if (!usuario) {
      alert('Usuário não está logado.');
      return;
    }

    if (estoqueAlvo === '') {
      setProdutos([]);
      setQuantidades({});
      setAnotacoes({});
      setEstoques({});
      setPrecoSacola(0);
      setPesoSacola(0);
      return;
    }

    try {
      const response = await axios.get(`https://provin.ind.br/api/produtos/sacola/${usuario}`, {
        params: { ordenacao, estoqueAlvo },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const produtosSacola = Array.isArray(response.data) ? response.data : [];

      // Produtos
      setProdutos(produtosSacola);
      setPrecoSacola(produtosSacola[0]?.preco_sacola || 0);
      setPesoSacola(produtosSacola[0]?.peso_sacola || 0);

      // Quantidades
      const qtd = produtosSacola.reduce((acc, produto) => {
        acc[produto.codigo] = produto.quantidade;
        return acc;
      }, {});
      setQuantidades(qtd);

      // Anotações
      const anot = produtosSacola.reduce((acc, produto) => {
        acc[produto.codigo] = produto.anotacao || '';
        return acc;
      }, {});
      setAnotacoes(anot);

      await fetchEstoques(produtosSacola, usuario);
    } catch (error) {
      console.error('Erro ao buscar a sacola:', error);
    }
  };

  const fetchEstoquesAlvoSacola = async () => {
    if (!usuario) return;

    try {
      const response = await axios.get(`https://provin.ind.br/api/produtos/sacola/${usuario}/estoques-alvo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const alvos = Array.isArray(response.data) ? response.data : [];
      setEstoquesAlvoSacola(alvos);

      setEstoqueAlvoSelecionado((alvoAtual) => {
        const valorDoAlvo = (alvo) => (
          alvo.estoque_alvo === null ? SEM_ESTOQUE_ALVO : String(alvo.estoque_alvo)
        );
        const alvoAtualValido = alvos.some((alvo) => valorDoAlvo(alvo) === alvoAtual);
        return alvoAtualValido ? alvoAtual : (alvos[0] ? valorDoAlvo(alvos[0]) : '');
      });
    } catch (error) {
      console.error('Erro ao buscar estoques alvo da sacola:', error);
    }
  };

  // Função de confirmação do modal
  const confirmarEsvaziarSacola = () => {
    setModalMessage('Tem certeza que deseja esvaziar a sacola?');
    setOnConfirmAction(() => esvaziarSacola);
    setIsModalOpen(true); // Abre o modal
  };

  // Função para abrir o modal de confirmação para remover um item específico
  const confirmarRemoverItem = (codigo) => {
    setModalMessage('Tem certeza que deseja remover este item?');
    setOnConfirmAction(() => () => removerDaSacola(codigo)); // Define a função com o código específico
    setIsModalOpen(true); // Abre o modal
  };

  const confirmarFinalizarPedido = async () => {
    if (!usuario) {
      alert('Usuário não está logado.');
      return;
    }

    if (sacolaSemEstoqueAlvo) {
      return;
    }

    if (produtos.length === 0) {
      setMensagemNotificacao('A sacola está vazia!');
      setNotificacaoErro(true);
      setTimeout(() => {
        setNotificacaoErro(false);
      }, 3000);
      return;
    }

    try {
      await axios.post(
        'https://provin.ind.br/api/produtos/sacola/validar-estoque-finalizacao',
        { estoqueAlvo: estoqueAlvoSelecionado },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setObservacaoPedido('');
      setIsModalPedidoOpen(true);
    } catch (error) {
      console.error('Erro ao validar estoque para finalização:', error);
      setMensagemNotificacao(
        error.response?.status === 409
          ? montarMensagemEstoqueInsuficiente(error.response?.data?.itens)
          : error.response?.data?.error || 'Erro ao validar estoque da sacola.'
      );
      setNotificacaoErro(true);
      setTimeout(() => {
        setNotificacaoErro(false);
      }, 7000);
    }
  };

  // Função para esvaziar a sacola
  const esvaziarSacola = async () => {
    setIsModalOpen(false); // Fecha o modal

    const token = localStorage.getItem('token');
    const usuario = localStorage.getItem('usuario');

    if (!usuario) {
      alert('Usuário não está logado.');
      return;
    }

    try {
      await axios.delete(`https://provin.ind.br/api/produtos/sacola/${usuario}`, {
        params: { estoqueAlvo: estoqueAlvoSelecionado },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchEstoquesAlvoSacola();
      fetchSacola();

      setMensagemNotificacao("Sacola esvaziada!");
      setNotificacaoErro(true);
      setTimeout(() => {
        setNotificacaoErro(false);
      }, 3000); // A mensagem desaparecerá após 3 segundo
    } catch (error) {
      console.error('Erro ao esvaziar a sacola:', error);
    }
  };

  // Atualizar a quantidade de um produto na sacola
  const atualizarQuantidade = async (codigo, quantidade) => {
    const token = localStorage.getItem('token');
    const usuario = localStorage.getItem('usuario');

    if (!usuario) {
      alert('Usuário não está logado.');
      return;
    }

    try {
      await axios.put('https://provin.ind.br/api/produtos/sacola',
        {
          usuario,
          codigo,
          quantidade,
          estoqueAlvo: estoqueAlvoSelecionado,
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      fetchSacola();

      setMensagemNotificacao("Quantidade atualizada!");
      setNotificacaoSucesso(true);
      setTimeout(() => {
        setNotificacaoSucesso(false);
      }, 5000);
    } catch (error) {
      console.error('Erro ao atualizar a quantidade:', error);
    }
  };

  // Atualizar o estado da quantidade
  const handleAlteracaoQuantidade = (codigo, valor) => {
    setQuantidades((prevQuantidades) => ({
      ...prevQuantidades,
      [codigo]: valor,
    }));
  };

  // Atualizar a anotação de um produto na sacola
  const atualizarAnotacao = async (codigo) => {
    const token = localStorage.getItem('token');
    const usuario = localStorage.getItem('usuario');

    if (!usuario) {
      alert('Usuário não está logado.');
      return;
    }

    try {
      await axios.put(
        'https://provin.ind.br/api/produtos/sacola/anotacao',
        {
          usuario,
          codigo,
          anotacao: anotacoes[codigo],
          estoqueAlvo: estoqueAlvoSelecionado,
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setMensagemNotificacao("Anotação atualizada!");
      setNotificacaoSucesso(true);
      setTimeout(() => {
        setNotificacaoSucesso(false);
      }, 5000); // A mensagem desaparecerá após 1 segundo
    } catch (error) {
      console.error('Erro ao atualizar anotação:', error);
    }
  };

  // Função para alterar o valor da anotação no estado
  const handleAlteracaoAnotacao = (codigo, valor) => {
    setAnotacoes((prevAnotacoes) => ({
      ...prevAnotacoes,
      [codigo]: valor,
    }));
  };

  // Remover um produto da sacola
  const removerDaSacola = async (codigo) => {
    setIsModalOpen(false); // Fecha o modal

    const token = localStorage.getItem('token');
    const usuario = localStorage.getItem('usuario');

    if (!usuario) {
      alert('Usuário não está logado.');
      return;
    }

    try {
      await axios.delete(`https://provin.ind.br/api/produtos/sacola/${usuario}/${encodeURIComponent(codigo)}`, {
        params: { estoqueAlvo: estoqueAlvoSelecionado },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchEstoquesAlvoSacola();
      fetchSacola();

      setMensagemNotificacao("Produto removido da sacola!");
      setNotificacaoErro(true);
      setTimeout(() => {
        setNotificacaoErro(false);
      }, 3000); // A mensagem desaparecerá após 3 segundos
    } catch (error) {
      console.error('Erro ao remover o produto da sacola:', error);
    }
  };

  const handlePrint = (e) => {
    window.print();
  };

  // Função para buscar estoques
  const fetchEstoques = async (produtos, usuario) => {
    const token = localStorage.getItem('token');

    try {
      const estoquePromises = produtos.map(produto =>
        axios.get(`https://provin.ind.br/api/api/produtos/sacola/listaEstoquesSacola`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: {
            usuario,
            codigo: produto.codigo,
          }
        })
      );

      const responses = await Promise.all(estoquePromises);
      const estoquesData = {};

      responses.forEach((response, index) => {
        const codigo = produtos[index].codigo;
        estoquesData[codigo] = response.data; // Presumindo que a resposta contém uma lista de estoques
      });

      setEstoques(estoquesData);
    } catch (error) {
      console.error('Erro ao buscar os estoques:', error);
    }
  };

  // Importar planilha
  const handleImportarPlanilha = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('usuario', usuario);
    if (estoqueAlvoSelecionado && !sacolaSemEstoqueAlvo) {
      formData.append('estoqueAlvo', estoqueAlvoSelecionado);
    }

    try {
      await axios.post('https://provin.ind.br/api/api/produtos/sacola/importarPlanilhaSacola', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      // Limpar o campo de arquivo após o upload
      fileInputRef.current.value = '';

      fetchEstoquesAlvoSacola();
    } catch (error) {
      console.error('Erro ao importar planilha:', error);
    }
  };

  const handlePlanilhaClick = () => {
    fileInputRef.current.click(); // Abre o seletor de arquivos ao clicar na área personalizada
  };

  const handleDownloadPlanilha = async () => {
    if (!usuario) {
      alert('Usuário não está logado.');
      return;
    }

    try {
      const response = await axios.get(`https://provin.ind.br/api/api/produtos/sacola/downloadPlanilhaSacola/${usuario}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { estoqueAlvo: estoqueAlvoSelecionado || undefined },
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || `sacola_${usuario}_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar planilha da sacola:', error);
      alert('Erro ao baixar planilha da sacola.');
    }
  };

  const finalizarPedido = async (observacoes = '') => {
    setIsModalOpen(false);
    setIsModalPedidoOpen(false);

    if (!usuario) {
      alert('Usuário não está logado.');
      return;
    }

    if (sacolaSemEstoqueAlvo) {
      return;
    }

    if (produtos.length === 0) {
      setMensagemNotificacao('A sacola está vazia!');
      setNotificacaoErro(true);
      setTimeout(() => {
        setNotificacaoErro(false);
      }, 3000);
      return;
    }

    try {
      const response = await axios.post(
        'https://provin.ind.br/api/produtos/sacola/finalizar-pedido',
        { usuario, observacoes, estoqueAlvo: estoqueAlvoSelecionado },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setProdutos([]);
      setQuantidades({});
      setAnotacoes({});
      setEstoques({});
      setPrecoSacola(0);
      setPesoSacola(0);
      fetchEstoquesAlvoSacola();

      setMensagemNotificacao(`Pedido #${response.data.pedidoId} finalizado!`);
      setNotificacaoSucesso(true);
      setTimeout(() => {
        setNotificacaoSucesso(false);
      }, 5000);
    } catch (error) {
      console.error('Erro ao finalizar pedido:', error);
      setMensagemNotificacao(
        error.response?.status === 409
          ? montarMensagemEstoqueInsuficiente(error.response?.data?.itens)
          : error.response?.data?.error || 'Erro ao finalizar pedido!'
      );
      setNotificacaoErro(true);
      setTimeout(() => {
        setNotificacaoErro(false);
      }, 7000);
    }
  };

  // Carrega os produtos da sacola ao montar o componente ou alterar a ordenação
  useEffect(() => {
    fetchEstoquesAlvoSacola();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSacola();
  }, [ordenacao, estoqueAlvoSelecionado]); // eslint-disable-line react-hooks/exhaustive-deps

  const permissoesSacola = permissoes.produtos;
  const temControlesExibicao = permissoesSacola.sacola_custo || permissoesSacola.sacola_preco || permissoesSacola.sacola_peso || permissoesSacola.sacola_estoques || permissoesSacola.sacola_total;

  return (
    <div className={styles.body}>
      <Header />

      <main className={styles.pageShell}>
        <Link redirectTo="/produtos" />

        <PageTitle src="/images/common/sacola.ico" title="Minha Sacola" />

        <section className={styles.toolbar} aria-label="Acoes da sacola">
          <div className={styles.toolbarInfo}>
            <span className={styles.badgeResumo}>
              <MdShoppingBag aria-hidden="true" />
              {produtos.length} {produtos.length === 1 ? 'item' : 'itens'}
            </span>

            <select
              value={ordenacao}
              onChange={(e) => setOrdenacao(e.target.value)}
              className={styles.selectOrdenacao}
              title="Ordenar por"
            >
              <option value="data_desc">Data (Mais recentes)</option>
              <option value="data_asc">Data (Mais antigos)</option>
              <option value="codigo_asc">Código (Crescente)</option>
              <option value="codigo_desc">Código (Decrescente)</option>
            </select>
          </div>

          <div className={styles.toolbarActions}>
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleImportarPlanilha}
              ref={fileInputRef}
              className={styles.inputArquivo}
            />

            <button className={styles.iconButton} onClick={handlePlanilhaClick} title="Importar Excel" aria-label="Importar Excel">
              <MdOutlineFileUpload aria-hidden="true" />
            </button>

            <button className={styles.iconButton} onClick={handleDownloadPlanilha} title="Baixar Excel" aria-label="Baixar Excel">
              <MdOutlineFileDownload aria-hidden="true" />
            </button>

            <button className={styles.iconButton} onClick={handlePrint} title="Imprimir" aria-label="Imprimir">
              <MdOutlinePrint aria-hidden="true" />
            </button>

            <button className={styles.esvaziarSacola} onClick={confirmarEsvaziarSacola}>
              <MdDeleteOutline aria-hidden="true" />
              Esvaziar
            </button>

            <button
              className={styles.finalizarPedido}
              type="button"
              onClick={confirmarFinalizarPedido}
              disabled={sacolaSemEstoqueAlvo || !estoqueAlvoSelecionado || ![10, 20, 29, 50, 90].includes(Number(estoqueAlvoSelecionado))}
              title={sacolaSemEstoqueAlvo ? 'Sacolas do catálogo não podem ser finalizadas como pedido' : 'Finalizar pedido'}
            >
              <MdOutlineShoppingCartCheckout aria-hidden="true" />
              Finalizar pedido
            </button>
          </div>
        </section>

        {estoquesAlvoSacola.length > 0 && (
          <section className={styles.estoqueAlvoTabs} aria-label="Estoques alvo da sacola">
            {estoquesAlvoSacola.map((alvo) => {
              const valorAlvo = alvo.estoque_alvo === null
                ? SEM_ESTOQUE_ALVO
                : String(alvo.estoque_alvo);

              return (
                <button
                  key={valorAlvo}
                  type="button"
                  className={estoqueAlvoSelecionado === valorAlvo ? styles.estoqueAlvoAtivo : ''}
                  onClick={() => setEstoqueAlvoSelecionado(valorAlvo)}
                >
                  {alvo.estoque_alvo === null ? 'Catálogo' : `Empresa ${alvo.estoque_alvo}`}
                  <span>{alvo.total_itens} {alvo.total_itens === 1 ? 'item' : 'itens'}</span>
                </button>
              );
            })}
          </section>
        )}

        {temControlesExibicao && (
          <section className={styles.controleExibicao} aria-label="Campos exibidos na sacola">
            {permissoesSacola.sacola_custo && (
              <label>
                <input type="checkbox" checked={mostrarCustoFinal} onChange={handleToggleCustoFinal} />
                Custo
              </label>
            )}

            {permissoesSacola.sacola_preco && (
              <label>
                <input type="checkbox" checked={mostrarPreco} onChange={handleTogglePreco} />
                Preço
              </label>
            )}

            {permissoesSacola.sacola_peso && (
              <label>
                <input type="checkbox" checked={mostrarPeso} onChange={handleTogglePeso} />
                Peso
              </label>
            )}

            {permissoesSacola.sacola_estoques && (
              <label>
                <input type="checkbox" checked={mostrarEstoques} onChange={handleToggleEstoques} />
                Estoques
              </label>
            )}

            {permissoesSacola.sacola_total && (
              <label>
                <input type="checkbox" checked={mostrarTotalSacola} onChange={handleToggleTotal} />
                Total da Sacola
              </label>
            )}
          </section>
        )}

        {mostrarTotalSacola && (
          <aside className={styles.totalSacola}>
            <h4>Total da Sacola</h4>
            <div>
              <span>Preço atacado</span>
              <strong>{formatarReal(precoSacola)}</strong>
            </div>
            <div>
              <span>Peso</span>
              <strong>{pesoSacola ? `${(pesoSacola * 1000).toFixed(2)}g` : ''}</strong>
            </div>
          </aside>
        )}

        {produtos.length === 0 && !notificacaoErro ? (
          <section className={styles.sacolaVazia}>
            <MdShoppingBag aria-hidden="true" />
            <p>Sua sacola está vazia!</p>
          </section>
        ) : (
          <section className={styles.containerProdutos}>
            {produtos.map((produto) => (
              <article key={produto.codigo} className={styles.cardProduto}>
                <div className={styles.produtoImagem}>
                  <img src={`https://provin.ind.br/api/imagem_produto/${produto.codigo.replace(/\//g, '+')}`} alt={produto.descr_longa || produto.codigo} />
                </div>

                <div className={styles.produtoConteudo}>
                  <div className={styles.produtoCabecalho}>
                    <span>{produto.codigo}</span>
                    <p>{produto.descr_longa}</p>
                  </div>

                  <div className={styles.funcoesProduto}>
                    <label className={styles.quantidadeField}>
                      Qtd.
                      <input
                        type="number"
                        min="1"
                        value={quantidades[produto.codigo] || 1}
                        onChange={(e) => handleAlteracaoQuantidade(produto.codigo, e.target.value)}
                        onBlur={() => atualizarQuantidade(produto.codigo, quantidades[produto.codigo] || 1)}
                      />
                    </label>

                    <button onClick={() => confirmarRemoverItem(produto.codigo)} title="Remover item">
                      <MdDeleteOutline aria-hidden="true" />
                      Remover
                    </button>
                  </div>

                  <textarea
                    className={!String(anotacoes[produto.codigo] || '').trim() ? styles.anotacaoVazia : ''}
                    type="text"
                    rows="2"
                    placeholder="Anotação do item"
                    value={anotacoes[produto.codigo] || ''}
                    onChange={(e) => handleAlteracaoAnotacao(produto.codigo, e.target.value)}
                    onBlur={() => atualizarAnotacao(produto.codigo)}
                  />

                  <div className={styles.infoGrid}>
                    {mostrarCustoFinal && produto.custo_final && (
                      <div className={styles.infoOpcional}>
                        <span>Custo</span>
                        <strong><b>Unit.</b>{formatarReal(produto.custo_final)}</strong>
                        <em><b>Total</b>{formatarReal(produto.custo_final_total)}</em>
                      </div>
                    )}

                    {mostrarPreco && produto.preco && (
                      <div className={styles.infoOpcional}>
                        <span>Preço</span>
                        <strong><b>Unit.</b>{formatarReal(produto.preco)}</strong>
                        <em><b>Total</b>{formatarReal(produto.preco_total)}</em>
                      </div>
                    )}

                    {mostrarPeso && produto.peso && (
                      <div className={styles.infoOpcional}>
                        <span>Peso</span>
                        <strong><b>Unit.</b>{produto.peso ? `${(produto.peso * 1000).toFixed(2)}g` : ''}</strong>
                        <em><b>Total</b>{produto.peso_total ? `${(produto.peso_total * 1000).toFixed(2)}g` : ''}</em>
                      </div>
                    )}
                  </div>

                  {mostrarEstoques && permissoesSacola.sacola_estoques && (
                    <div className={styles.listaEstoques}>
                      <h5>
                        <MdOutlineInventory2 aria-hidden="true" />
                        Estoques
                      </h5>
                      {estoques[produto.codigo] && estoques[produto.codigo].data.length > 0 ? (
                        <ul className={styles.estoqueContainer}>
                          {estoques[produto.codigo].data.map((estoque) => (
                            <li key={estoque.empresa} className={styles.estoqueCard}>
                              <span className={styles.empresa}>{estoque.empresa}</span>
                              <span className={styles.qtdEstoque}>{estoque.qtd} pçs</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>Nenhuma empresa com estoque!</p>
                      )}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </section>
        )}

        <NotificacaoSucesso
          message={mensagemNotificacao}
          show={notificacaoSucesso}
          onClose={() => setNotificacaoSucesso(false)}
        />

        <NotificacaoErro
          message={mensagemNotificacao}
          show={notificacaoErro}
          onClose={() => setNotificacaoErro(false)}
        />

        <ModalConfirmacao
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={onConfirmAction}
          message={modalMessage}
        />

        {isModalPedidoOpen && (
          <div className={styles.modalPedidoOverlay} role="dialog" aria-modal="true" aria-label="Finalizar pedido">
            <div className={styles.modalPedido}>
              <h3>Finalizar pedido</h3>
              <div className={styles.sacolaFinalizacaoDestaque}>
                <MdOutlineShoppingCartCheckout aria-hidden="true" />
                <div>
                  <span>Sacola selecionada</span>
                  <strong>{tituloSacolaSelecionada}</strong>
                  <small>{produtos.length} {produtos.length === 1 ? 'item' : 'itens'} para conferir</small>
                </div>
              </div>
              <p>Adicione uma observação opcional antes de gerar o pedido.</p>

              <textarea
                rows="5"
                maxLength={500}
                value={observacaoPedido}
                onChange={(e) => setObservacaoPedido(e.target.value)}
                placeholder="Observação do pedido"
                autoFocus
              />

              <div className={styles.modalPedidoFooter}>
                <span>{observacaoPedido.length}/500</span>
                <div>
                  <button type="button" className={styles.botaoCancelarPedido} onClick={() => setIsModalPedidoOpen(false)}>
                    Cancelar
                  </button>
                  <button type="button" className={styles.botaoConfirmarPedido} onClick={() => finalizarPedido(observacaoPedido)}>
                    Finalizar pedido
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Sacola;
